import { Currency } from '@kodiak-finance/sdk-core'
import { useCallback, useEffect, useState } from 'react'

const MAX_RECENT_TOKENS = 5
const NATIVE_KEY = 'NATIVE'
const CHANGE_EVENT = 'recent-tokens-change'

const storageKey = (chainId: number) => `recent-tokens:${chainId}`

/** Lowercase address, or NATIVE for the chain's native currency. */
export function recentTokenKey(currency: Currency): string {
  return currency.isNative ? NATIVE_KEY : currency.address.toLowerCase()
}

export function isNativeRecentKey(key: string): boolean {
  return key === NATIVE_KEY
}

// Storage can be missing or blocked (private mode), so recents degrade to empty.
function readRecent(chainId: number): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(chainId)) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : []
  } catch {
    return []
  }
}

function writeRecent(chainId: number, keys: string[]) {
  try {
    localStorage.setItem(storageKey(chainId), JSON.stringify(keys))
  } catch {
    return
  }
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

/** The last tokens picked in the token selector on a chain, newest first. */
export function useRecentTokens(chainId: number | undefined) {
  const [keys, setKeys] = useState<string[]>(() => (chainId ? readRecent(chainId) : []))

  useEffect(() => {
    if (!chainId) {
      setKeys([])
      return undefined
    }
    const sync = () => setKeys(readRecent(chainId))
    sync()
    window.addEventListener(CHANGE_EVENT, sync)
    return () => window.removeEventListener(CHANGE_EVENT, sync)
  }, [chainId])

  const addRecent = useCallback(
    (currency: Currency) => {
      if (!chainId) return
      const key = recentTokenKey(currency)
      writeRecent(
        chainId,
        [key, ...readRecent(chainId).filter((existing) => existing !== key)].slice(0, MAX_RECENT_TOKENS)
      )
    },
    [chainId]
  )

  const clearRecent = useCallback(() => {
    if (chainId) writeRecent(chainId, [])
  }, [chainId])

  return { recentKeys: keys, addRecent, clearRecent }
}
